<template>
  <div class="mx-auto max-w-7xl p-4 md:p-6">
    <RoiCalculator
      title="Dealer ROI"
      subtitle="Use the sliders or direct inputs to update your projections"
      input-title="Calculator Inputs"
      output-title="Projection Outputs"
      main-output-key="roi"
      :input-configs="inputConfigs"
      :output-configs="outputConfigs"
    />
  </div>
</template>

<script setup lang="ts">
// components
import RoiCalculator from '@/components/unique/roi-calculator.vue'
import type { RoiInputConfig, RoiOutputConfig } from '@/components/unique/roi-calculator.vue'

/**
 * Fixed spend amount per appointment used by the spend formula.
 */
const APPOINTMENT_SPEND = 39

/**
 * Percentage represented as decimal for ending customers in 24 months.
 */
const ENDING_CUSTOMERS_PERCENTAGE = 0.048

/**
 * Input controls for the first ROI calculator variant.
 */
const inputConfigs: RoiInputConfig[] = [
  {
    key: 'appointmentVolume',
    label: 'Appointment Volume',
    min: 0,
    max: 200,
    step: 1,
    defaultValue: 25,
    decimals: 0
  },
  {
    key: 'conversionRate',
    label: 'Conversion Rate',
    min: 0,
    max: 100,
    step: 0.1,
    defaultValue: 44,
    decimals: 0,
    suffix: '%'
  },
  {
    key: 'avgProfitPerUnit',
    label: 'Avg Profit per Unit',
    min: 0,
    max: 10000,
    step: 50,
    defaultValue: 1450,
    decimals: 0,
    prefix: '£'
  }
]

/**
 * Output formulas for ROI and supporting metrics.
 */
const outputConfigs: RoiOutputConfig[] = [
  {
    key: 'deals',
    label: 'Deals',
    format: 'number',
    decimals: 0,
    calculate: (inputs: Record<string, number>) => {
      try {
        return inputs.appointmentVolume * (inputs.conversionRate / 100)
      } catch (error) {
        console.error('Failed to calculate deals', error)
        return 0
      }
    }
  },
  {
    key: 'spend',
    label: 'Spend',
    format: 'currency',
    decimals: 0,
    calculate: (inputs: Record<string, number>) => {
      try {
        return inputs.appointmentVolume * APPOINTMENT_SPEND
      } catch (error) {
        console.error('Failed to calculate spend', error)
        return 0
      }
    }
  },
  {
    key: 'roi',
    label: 'ROI',
    format: 'currency',
    decimals: 0,
    calculate: (inputs: Record<string, number>, outputs: Record<string, number>) => {
      try {
        return outputs.deals * inputs.avgProfitPerUnit - outputs.spend
      } catch (error) {
        console.error('Failed to calculate ROI', error)
        return 0
      }
    }
  },
  {
    key: 'costPerLead',
    label: 'Cost per Lead',
    format: 'currency',
    decimals: 2,
    calculate: (inputs: Record<string, number>, outputs: Record<string, number>) => {
      try {
        if (inputs.appointmentVolume <= 0) {
          return 0
        }
        return outputs.spend / inputs.appointmentVolume
      } catch (error) {
        console.error('Failed to calculate cost per lead', error)
        return 0
      }
    }
  },
  {
    key: 'customersEnding24m',
    label: 'Customers ending in the next 24m',
    format: 'number',
    decimals: 0,
    calculate: (inputs: Record<string, number>) => {
      try {
        if (ENDING_CUSTOMERS_PERCENTAGE <= 0) {
          return 0
        }
        return inputs.appointmentVolume / ENDING_CUSTOMERS_PERCENTAGE
      } catch (error) {
        console.error('Failed to calculate ending customers', error)
        return 0
      }
    }
  }
]
</script>
